/**
 * ScansTableView – AG Grid column definitions and rendering for the Scans tab.
 * Matches Figma Design 1: Name, Type, Scan Time, Status, Assets Discovered,
 * Assets Assessed, Misconfigurations, Agent, User, Actions (kebab menu).
 */

import React, { useState, useMemo } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { IconButton, Menu, MenuItem, Tooltip, Divider, Checkbox, Button } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import calenderIcon from "../../../assets/baseVersion/calender.png";
import buleCalenderIcon from "../../../assets/bule_schedule_calender.svg";
import misconfig from "../../../assets/misconfig.svg";
import { getScanStatusStyle, formatScanTime, formatScanType, parseFlexibleDate } from "./scansV2.utils";
import type { ScanRow } from "./scansV2.types";

export interface ScanActionHandlers {
  onRename?: (row: ScanRow) => void;
  onScanInfo?: (row: ScanRow) => void;
  onSchedule?: (row: ScanRow) => void;
  onRescan?: (row: ScanRow) => void;
  onCancel?: (row: ScanRow) => void;
  getScheduleLabel?: (row: ScanRow) => string;
  shouldShowSchedule?: (row: ScanRow) => boolean;
  shouldShowRescan?: (row: ScanRow) => boolean;
  shouldShowCancel?: (row: ScanRow) => boolean;
}

// ──────────────────────────────────────────────
// Cell Renderers
// ──────────────────────────────────────────────

/** Name column with tooltip */
const NameCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return null;
  return (
    <Tooltip
      title={params.value}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {params.value}
      </span>
    </Tooltip>
  );
};

/** Type column with formatted label and tooltip */
const TypeCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return <span>-</span>;
  const displayValue = formatScanType(params.value);
  return (
    <Tooltip
      title={displayValue}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {displayValue}
      </span>
    </Tooltip>
  );
};

/** Scan Time column with calendar icon */
const ScanTimeCellRenderer = (params: ICellRendererParams) => {
  if (!params.value) return null;
  const formattedTime = formatScanTime(params.value);
  const effectiveScheduleStatus = String(
    params.data?.schedule_status ?? params.data?.schedule?.status ?? ""
  )
    .trim()
    .toLowerCase();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {params.data.schedule !== null && effectiveScheduleStatus === "scheduled" && (
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-line", fontSize: "12px", padding: 5 }}>
              <strong>Schedule Information</strong>
              <Divider sx={{ color: "#fff" }} />
              Frequency: {params.data.schedule?.frequency_name || "N/A"}
              <br />
              On date: {params.data.schedule?.values?.date || "Everyday"}
              <br />
              At time: {params.data.schedule?.values?.time || "12:00 AM"}
            </div>
          }
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: { boundary: "window" },
                },
              ],
            },
            tooltip: {
              sx: {
                backgroundColor: "#fff",
                color: "#000",
                border: "1px solid #ddd",
                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                fontSize: "14px",
              },
            },
            arrow: { sx: { color: "#fff" } },
          }}
        >
          <img
            src={calenderIcon}
            alt="calendar"
            style={{ width: "15px", height: "15px" }}
          />
        </Tooltip>
      )}

      {effectiveScheduleStatus === "schedulable" && (
        <img
          src={buleCalenderIcon}
          alt="calendar"
          style={{ width: "15px", height: "15px" }}
        />
      )}

      <span>{formattedTime}</span>
    </div>
  );
};

/** Status column with colored badge + asset count tooltip */
const StatusCellRenderer = (params: ICellRendererParams) => {
  const data = params.data as ScanRow;
  if (!data) return null;
  if (!params.value) return null;

  const style = getScanStatusStyle(params.value);

  // Build asset tooltip text like "Assets: 2/2" or "Assets: 0/1"
  const passed = data.passedAssets ?? 0;
  const Total = data.TotalAssets ?? 0;
  const tooltipText = `${passed}/${Total}`;

  const badge = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "0 10px",
        borderRadius: "5px",
        fontWeight: 700,
        fontSize: "13px",
        color: style.color,
        background: style.background,
        border: style.border,
        minWidth: "80px",
        textAlign: "center",
        height: "24px",
        lineHeight: "20px",
        boxSizing: "border-box",
        cursor: "default",
      }}
    >
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          backgroundColor: style.dotColor,
          display: "inline-block",
        }}
      />
      {params.value}
    </span>
  );

  return (
    <Tooltip
      title={tooltipText}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "13px",
            fontWeight: 600,
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      {badge}
    </Tooltip>
  );
};

/** Misconfigurations column with warning icon */
const MisconfigCellRenderer = (params: ICellRendererParams) => {
  const scanType = String(params.data?.type || "").toLowerCase().replace(/_/g, " ");
  if (scanType.includes("discovery") && !scanType.includes("posture")) {
    return <span>-</span>;
  }
  if (params.value === undefined || params.value === null) return <span>-</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <img
        src={misconfig}
        alt="Misconfiguration"
        style={{ width: "20px", height: "20px" }}
      />
      <span>{params.value}</span>
    </div>
  );
};

/** User column with tooltip */
const UserCellRenderer = (params: ICellRendererParams) => {
  return (
    <Tooltip
      title={params.value}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #ddd",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          },
        },
        arrow: { sx: { color: "#fff" } },
      }}
    >
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {params.value}
      </span>
    </Tooltip>
  );
};

/** Simple dash for null values */
const NullableCellRenderer = (params: ICellRendererParams) => {
  const scanType = String(params.data?.type || "").toLowerCase();
  if (
    params.value === null ||
    params.value === undefined ||
    (scanType === "onprem_posture" && Number(params.value) === 0)
  ) {
    return <span>-</span>;
  }
  return <span>{params.value}</span>;
};

/** Assets Assessed cell renderer: shows '-' for pure discovery scans */
const AssetsAssessedCellRenderer = (params: ICellRendererParams) => {
  const scanType = String(params.data?.type || "").toLowerCase();
  if (scanType.includes("discovery") && !scanType.includes("posture")) {
    return <span>-</span>;
  }
  if (params.value === null || params.value === undefined) return <span>-</span>;
  return <span>{params.value}</span>;
};

// ──────────────────────────────────────────────
// Kebab Action Menu for Scans
// ──────────────────────────────────────────────

const ScanActionMenu = ({ params }: { params: ICellRendererParams }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const data = params.data as ScanRow;
  const cellParams = params as ICellRendererParams & ScanActionHandlers;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    if (cellParams.onRename) {
      cellParams.onRename(data);
    }
    handleClose();
  };

  const handleScanInfo = () => {
    if (cellParams.onScanInfo) {
      cellParams.onScanInfo(data);
    }
    handleClose();
  };

  const handleScheduleScan = () => {
    if (cellParams.onSchedule) {
      cellParams.onSchedule(data);
    }
    handleClose();
  };

  const handleRescan = () => {
    if (cellParams.onRescan) {
      cellParams.onRescan(data);
    }
    handleClose();
  };

  const handleCancel = () => {
    if (cellParams.onCancel) {
      cellParams.onCancel(data);
    }
    handleClose();
  };

  if (!data) return null;
  // Show kebab menu for all types
  const normalizedType = String(data.type ?? "").toLowerCase();

  const isCancelled = data.status === "Cancelled";

  const scheduleLabel = cellParams.getScheduleLabel
    ? cellParams.getScheduleLabel(data)
    : "Schedule Scan";
  const showSchedule = !isCancelled && (cellParams.shouldShowSchedule
    ? cellParams.shouldShowSchedule(data)
    : true);
  const showRescan = !isCancelled && (cellParams.shouldShowRescan
    ? cellParams.shouldShowRescan(data)
    : Boolean(data.reScan));
  const showCancel = cellParams.shouldShowCancel
    ? cellParams.shouldShowCancel(data)
    : data.status === "In Progress";

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        data-prevent-row-click="true"
        sx={{ "&:focus": { outline: "none" }, cursor: "pointer" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
              minWidth: 160,
            },
          },
        }}
      >
        <MenuItem onClick={handleRename} sx={{ fontSize: "14px" }}>
          Rename
        </MenuItem>
        {data.status !== "Cancelled" && data.type !== "aws_discovery_posture" && (!normalizedType.includes("discovery") || normalizedType.includes("posture")) && (
          <MenuItem onClick={handleScanInfo} sx={{ fontSize: "14px" }}>
            Scan Information
          </MenuItem>
        )}
        {showSchedule && (
          <MenuItem onClick={handleScheduleScan} sx={{ fontSize: "14px" }}>
            {scheduleLabel}
          </MenuItem>
        )}
        {showRescan && data.type !== "aws_discovery_posture" && (
          <MenuItem onClick={handleRescan} sx={{ fontSize: "14px" }}>
            Rescan
          </MenuItem>
        )}
        {showCancel && (
          <MenuItem onClick={handleCancel} sx={{ fontSize: "14px", color: "#f53b3b" }}>
            Cancel
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// ──────────────────────────────────────────────
// Hook: useScansColumnDefs
// ──────────────────────────────────────────────

export const useScansColumnDefs = (actionHandlers: ScanActionHandlers = {}): ColDef[] => {
  return useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        filter: true,
        sortable: true,
        minWidth: 120,
        cellRenderer: NameCellRenderer,
      },
      {
        headerName: "Type",
        field: "type",
        filter: true,
        sortable: false,
        minWidth: 180,
        width: 180,
        cellRenderer: TypeCellRenderer,
      },
      {
        headerName: "Scan Time",
        field: "scanTime",
        filter: false,
        sortable: true,
        minWidth: 180,
        comparator: (valueA: any, valueB: any) => {
          const a = parseFlexibleDate(valueA);
          const b = parseFlexibleDate(valueB);
          if (Number.isNaN(a) && Number.isNaN(b)) return 0;
          if (Number.isNaN(a)) return -1;
          if (Number.isNaN(b)) return 1;
          return a - b;
        },
        cellRenderer: ScanTimeCellRenderer,
      },
      {
        headerName: "Status",
        field: "status",
        filter: true,
        sortable: false,
        minWidth: 170,
        cellClass: "ag-cell-status-badge",
        cellRenderer: StatusCellRenderer,
      },
      {
        headerName: "Assets Discovered",
        field: "assetsDiscovered",
        filter: true,
        sortable: true,
        minWidth: 140,
        cellRenderer: NullableCellRenderer,
      },
      {
        headerName: "Assets Assessed",
        field: "assetsAssessed",
        filter: true,
        sortable: true,
        minWidth: 140,
        cellRenderer: AssetsAssessedCellRenderer,
      },
      {
        headerName: "Misconfigurations",
        field: "misconfigurations",
        filter: true,
        sortable: true,
        minWidth: 140,
        cellRenderer: MisconfigCellRenderer,
      },
      {
        headerName: "Agent",
        field: "agent",
        filter: true,
        sortable: true,
        minWidth: 120,
      },
      {
        headerName: "User",
        field: "user",
        filter: true,
        sortable: true,
        minWidth: 140,
        cellRenderer: UserCellRenderer,
      },
      {
        headerName: "",
        field: "actions",
        filter: false,
        sortable: false,
        width: 50,
        maxWidth: 50,
        cellRendererParams: {
          onRename: actionHandlers.onRename,
          onScanInfo: actionHandlers.onScanInfo,
          onSchedule: actionHandlers.onSchedule,
          onRescan: actionHandlers.onRescan,
          onCancel: actionHandlers.onCancel,
          getScheduleLabel: actionHandlers.getScheduleLabel,
          shouldShowSchedule: actionHandlers.shouldShowSchedule,
          shouldShowRescan: actionHandlers.shouldShowRescan,
          shouldShowCancel: actionHandlers.shouldShowCancel,
        },
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.data) return null;
          return <ScanActionMenu params={params} />;
        },
      },
    ],
    [actionHandlers]
  );
};
